import dayjs from "dayjs";
import { ISentiment, ICall, PaymentStatus } from "../types/call";

export const randomSentiment = () => {
  const sentiments = ["positive", "neutral", "negative"];
  return sentiments[
    Math.floor(Math.random() * sentiments.length)
  ] as ISentiment;
};

/**
 * Safely formats a duration string in "MM:SS" format to "Xm Ys" format
 * @param duration - Duration string in "MM:SS" format or null/undefined
 * @returns Formatted duration string or empty string if duration is null/undefined
 */
export const formatDuration = (duration: string | null | undefined): string => {
  if (!duration) {
    return "";
  }

  const parts = duration.split(":");
  if (parts.length !== 2) {
    return "";
  }

  const minutes = parseInt(parts[0]);
  const seconds = parts[1];

  if (isNaN(minutes)) {
    return "";
  }

  return `${minutes}m ${seconds}s`;
};

export const isCallProcessing = (
  call: ICall,
  isStreamOrdering: boolean
): boolean => {
  return isStreamOrdering && call.orderEntryStatus === "PROCESSING";
};

export const isCallWithOrder = (call: ICall): boolean => {
  return call.order != null || isOrderSubmittedViaTablet(call);
};

export const isCallCompleted = (call: ICall): boolean => {
  return call.timestamp != null && call.timestamp !== "";
};

export const isCallOngoing = (call: ICall): boolean => {
  return (
    !isCallCompleted(call) &&
    dayjs.utc(call.startedAt).isAfter(dayjs.utc().subtract(15, "minutes"))
  );
};

export const isPrepaid = (call: ICall): boolean => {
  const callPayments = call?.payments ?? [];
  if (callPayments.length > 0) {
    const latestPayment = callPayments.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];
    return latestPayment?.status === PaymentStatus.SUCCEEDED;
  }
  return false;
};

export const hasBeenRefunded = (call: ICall): boolean => {
  const callPayments = call?.payments ?? [];
  if (callPayments.length > 0) {
    const latestPayment = callPayments.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];
    return latestPayment?.status === PaymentStatus.REFUNDED;
  }
  return false;
};

export const hasPaymentFailed = (call: ICall): boolean => {
  const callPayments = call?.payments ?? [];
  if (callPayments.length > 0) {
    const latestPayment = callPayments.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];
    return latestPayment?.status === PaymentStatus.FAILED;
  }
  return false;
};

export const isExpired = (call: ICall): boolean => {
  return call?.order?.state == "EXPIRED";
};

export const isVoided = (call: ICall): boolean => {
  return call?.order?.state == "VOIDED";
};

export const isOrderSubmittedViaTablet = (call: ICall): boolean => {
  return (
    call.orderEntryStatus === "SUBMITTED" &&
    (call.order == null ||
      ["FAILED", "FAILED_POS", "PROCESSING"].includes(call.order?.state ?? "")) // if LO order failed, then it was submitted via tablet
  );
};

// Filter out retell error calls, keeping only completed or ongoing calls
export const filterValidCalls = (calls: ICall[]): ICall[] => {
  return calls.filter((call) => isCallCompleted(call) || isCallOngoing(call));
};
